import { handleAxiosError, notEqual } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { Integrations } from '@/client/accounts/types/account-user-management.type';
import { Group } from '@/client/groups';
import { Role } from '@/client/roles';
import {
  Actions,
  Subjects,
  SystemRoles,
  User,
  UserFormState,
} from '@/client/users';
import { UserOrigin } from '@/client/users/types/origins.enum';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { UserForm } from '@/components/users/forms';
import { useAzureADHandler } from '@/hooks/integrations/useAzureAD.hook';
import {
  useGroupsPartialRequest,
  useMe,
  useRoles,
  useUpdateUser,
  useUser,
} from '@/hooks/query';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { LoadingPage } from '@/pages';
import { selectCurrentAccount } from '@/store/features/account';
import { setCurrentUser } from '@/store/features/users';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getAccountIntegration,
  isMultiAdminRestriction,
  queryStateConverter,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { FormikValues } from 'formik';
import { MenuItem } from 'primereact/menuitem';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { UserTabs } from './UserTabs';

const getBreadcrumbs = (
  account: Account,
  user: User,
  t: TranslationFunctionType,
  returnUrl?: string,
): MenuItem[] => {
  const items = [
    {
      label: account?.name,
      url: account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('users'),
      url: returnUrl || RedirectPaths[RedirectPathsEnum.USERS](),
      template: AppBreadCrumbTemplate,
    },
    {
      label: user?.name,
      url: RedirectPaths[RedirectPathsEnum.USERS_EDIT](user?.id),
      template: AppBreadCrumbTemplate,
    },
  ];

  return items;
};

const resolveSSOOrigin = (account: Account | null): UserOrigin | null => {
  const integration = getAccountIntegration(account);
  if (integration?.source === Integrations.GOOGLE_WORKSPACE)
    return UserOrigin.GOOGLE_WORKSPACE;
  if (integration?.source === Integrations.ACTIVE_DIRECTORY)
    return UserOrigin.AZURE_AD;
  if (account?.meta?.isSSO) return UserOrigin.DEFAULT;
  return null;
};

export const UpdateUserPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { cannot } = usePermission();
  const location = useLocation();
  const { id } = useParams();
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);

  const locationState = location.state as {
    pathname?: string;
    search?: string;
  };
  const returnUrl =
    locationState?.pathname && locationState?.search
      ? locationState.pathname + locationState.search
      : undefined;
  const { user: currentUser } = useMe({});
  const { roles } = useRoles({
    take: 20,
  });

  const { groups, isLoading: isGroupsLoading } = useGroupsPartialRequest({
    accountId: account?.id,
    sort: ['name,asc'],
    filters: [notEqual('isForPhishing', true)],
  });

  const { user, isLoading } = useUser({
    userId: id,
    accountId: account?.id,
  });
  const updateUser = useUpdateUser();

  const [initialValues, setInitialValues] = useState<UserFormState>({
    firstName: '',
    lastName: '',
    name: '',
    username: '',
    email: '',
    password: undefined,
    role: undefined,
    active: false,
    branch: undefined,
    language: '',
    isManual: false,
    assignedAccounts: undefined,
    expiresOn: undefined,
    reactivatesOn: undefined,
    isTemporary: false,
    freeTrialCheck: false,
    manuallyDeactivated: false,
    manuallyDeactivatedByUserName: '',
    manuallyDeactivatedOn: undefined,
  });

  useEffect(() => {
    if (!user) return;

    setInitialValues({
      firstName: user.firstName,
      lastName: user.lastName,
      name: user.name,
      username: user.username,
      email: user.email,
      active: user.active,
      role: user.role,
      language: user.language,
      branch: user.branch ?? undefined,
      groups: user.groups ?? undefined,
      isManual: user.origin === UserOrigin.MANUAL,
      assignedAccounts: user.assignedAccounts,
      isTemporary: !!user.expiresOn,
      expiresOn: user.expiresOn,
      reactivatesOn: user.reactivatesOn,
      manuallyDeactivated: user.meta?.manuallyDeactivated,
      manuallyDeactivatedByUserName: user.meta?.manuallyDeactivatedByUser?.name,
      manuallyDeactivatedOn: user.meta?.manuallyDeactivatedOn,
      domainVerified: user.domainVerified,
    });
  }, [user]);

  const handleUserAction = async (data: FormikValues, id?: string) => {
    const result = await updateUser.update({
      userId: id as string,
      accountId: (account as Account)?.id,
      updates: {
        ...data,
        role: data.role.id,
        branch: data.branch ? data.branch.id : null,
        groups: data.groups ? data.groups.map(({ id }: Group) => id) : null,
        origin:
          account?.meta?.isSSO && data.isManual !== initialValues.isManual
            ? data.isManual
              ? UserOrigin.MANUAL
              : resolveSSOOrigin(account) ?? undefined
            : undefined,
        assignedAccounts: data.assignedAccounts
          ? data.assignedAccounts.map(({ id }: Account) => id)
          : null,
        expiresOn:
          data.isTemporary &&
          (data.role?.code === SystemRoles.ADMIN ||
            data.role?.code === SystemRoles.MULTIPLE_ACCOUNTS_ADMIN)
            ? data.expiresOn
            : null,
        reactivatesOn: data.reactivatesOn || null,
        isTemporaryPassword:
          (user?.id !== currentUser?.id && !!data.password) || undefined,
      },
    });

    return result;
  };

  const handleSubmit = async (data: FormikValues) => {
    const isSwitchingToSSO =
      account?.meta?.isSSO &&
      data.isManual !== initialValues.isManual &&
      !data.isManual;

    if (isSwitchingToSSO && !resolveSSOOrigin(account)) {
      toast?.error(t('error'), t('error.badRequest'));
      return;
    }

    try {
      const newUser = await handleUserAction(data, user ? user.id : undefined);

      if (currentUser?.id === newUser.id) {
        dispatch(setCurrentUser({ ...currentUser, ...newUser }));
      }

      toast?.success(
        t('toast.success'),
        user ? t('user.updated') : t('user.created'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  // TODO: In near future this should include Google Workspace fields as well
  const [syncedAdFields, setSyncedAdFields] = useState<string[]>([]);
  const { accountAd, isADLoading } = useAzureADHandler(account?.id as string);

  useEffect(() => {
    if (accountAd && accountAd.active) {
      setSyncedAdFields(
        accountAd.attributesMap
          ?.filter((attribute) => attribute.synced)
          .map((attribute) => attribute.entityField ?? '') ?? [],
      );
    }
  }, [accountAd]);

  if (!user || isLoading || isGroupsLoading || isADLoading) {
    return <LoadingPage message={t('user.loading')} />;
  }

  const isReadOnlyView =
    cannot(Actions.UPDATE, Subjects.USERS) ||
    (account?.isSystem && currentUser?.role.code !== SystemRoles.DEVELOPER);

  const isMultiAdminRestrictionCheck = isMultiAdminRestriction(
    user || undefined,
    currentUser,
  );

  return (
    <>
      <AppBreadCrumb
        model={getBreadcrumbs(account as Account, user, t, returnUrl)}
      />
      <h1>{initialValues.name ? initialValues.name : initialValues.email}</h1>
      <UserTabs user={user} />

      <FlexContainer justify="flex-start">
        <UserForm
          isMultiAdminRestriction={isMultiAdminRestrictionCheck}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          state={queryStateConverter(updateUser)}
          isCreate={false}
          roles={roles?.result as Role[]}
          groups={groups as Group[]}
          disabled={isReadOnlyView}
          isReadOnlyView={isReadOnlyView}
          isSSOEnabled={!!user?.account?.meta?.isSSO}
          origin={user?.origin}
          syncedAdFields={syncedAdFields}
          isAdSyncedField={accountAd?.active}
        />
      </FlexContainer>
    </>
  );
};
